import { z } from 'zod';
import { TaskBulkOp, TaskBulkOpType } from './entities/task-module/bulk.js';
import { TaskFilterShared } from './entities/task-module/task-filter.js';
import { IsoDateTime, UuidString } from './primitives.js';

/**
 * chat-interactive-actions B2 (SOLUTION §2.4) — конверт «чат → действие в UI».
 *
 * До пака канала не существовало: FE-адаптер выбрасывал незнакомые события, а
 * инструменты чата могли только вернуть текст модели. Здесь — типизированный
 * union действий (I4): payload валидируется Zod на бэкенде ДО эмита, фронт
 * игнорирует незнакомый `type` без падения потока.
 *
 * Каркас generic (draft-таблица, конверт, карточка подтверждения), конкретизация
 * v1 — модуль задач. Другие сущности добавляют свои варианты в этот же union.
 */

// ─── Warnings превью (I6: коды, не строки) ──────────────────────────

export const ChatActionWarningCode = z.enum([
  /** Выборка больше потолка пачки (В-2: 500) — список усечён, «уточни фильтр». */
  'over_cap',
  /** Часть выборки видима, но без права записи — применится не ко всем. */
  'partial_writable',
  /** Правило фильтра по полю, которое проектная поверхность не применит клиентски. */
  'project_surface_unsupported_field',
]);
export type ChatActionWarningCode = z.infer<typeof ChatActionWarningCode>;

// ─── Черновик действия — вид и статус-машина (§2.2) ────────────────

export const ChatActionDraftKind = z.enum(['task_bulk', 'task_bulk_comment']);
export type ChatActionDraftKind = z.infer<typeof ChatActionDraftKind>;

/** proposed → consumed | cancelled | expired (лениво при чтении, TTL 1h — В-5). */
export const ChatActionDraftStatus = z.enum(['proposed', 'consumed', 'cancelled', 'expired']);
export type ChatActionDraftStatus = z.infer<typeof ChatActionDraftStatus>;

// ─── Поверхность задач ──────────────────────────────────────────────

export const ChatTaskSurface = z.enum(['global', 'project']);
export type ChatTaskSurface = z.infer<typeof ChatTaskSurface>;

/**
 * Маршруты, на которые чат имеет право отправить пользователя (NavigateAction).
 * Закрытый перечень: строка вне него на фронте не исполняется (F5). `/work-tasks`
 * с `project_id` — проектная поверхность `/work-tasks/$projectId`.
 */
export const ChatNavigateRoute = z.enum([
  '/work-tasks',
  '/stock-entries',
  '/work-orders',
  '/stock-reconciliation',
  '/items',
  '/warehouses',
  '/landed-cost',
  '/approvals',
]);
export type ChatNavigateRoute = z.infer<typeof ChatNavigateRoute>;

// ─── UiAction ───────────────────────────────────────────────────────

/**
 * W1 — применить фильтр (обратимо, БД не трогает; применяется сразу — В-1).
 * `show_closed` — эфемерный override тумблера «показывать закрытые» [П10]: фронт
 * не персистит его в workTasksViewStore, «Сбросить» возвращает прежнее значение.
 */
export const ApplyTaskFilterAction = z
  .object({
    type: z.literal('apply_task_filter'),
    surface: ChatTaskSurface,
    project_id: UuidString.optional(),
    filter: TaskFilterShared,
    show_closed: z.boolean().optional(),
    warnings: z.array(ChatActionWarningCode),
  })
  .strict()
  .refine((a) => a.surface !== 'project' || a.project_id !== undefined, {
    message: 'project surface requires project_id',
    path: ['project_id'],
  });
export type ApplyTaskFilterAction = z.infer<typeof ApplyTaskFilterAction>;

/**
 * Текущее значение ИЗМЕНЯЕМОГО поля у задачи из примера (R2, UX-2 приёмки).
 *
 * Карточка показывала только сторону «станет», а плёнка при этом требовала
 * «вернуть значения вручную» — те самые, которых карточка не назвала. §4.8 просит
 * пару current → proposed, и без левой стороны решение принимается вслепую.
 *
 * Значение СТРУКТУРНОЕ (код приоритета, код группы статуса, ISO-дата), не текст:
 * бэкенд не шлёт user-facing строк (инвариант i18n #19), локализует фронт.
 * `null` — поле у задачи пустое (нет срока).
 */
export const ChatActionSampleCurrent = z
  .object({
    field: z.enum(['priority', 'state_group', 'due_date']),
    value: z.string().max(40).nullable(),
  })
  .strict();
export type ChatActionSampleCurrent = z.infer<typeof ChatActionSampleCurrent>;

/**
 * Строка сэмпла превью — id + название (≤ 5 штук, I3).
 *
 * Поля ниже необязательны намеренно: черновики, созданные ДО заведения этих полей,
 * лежат в БД и читаются той же схемой при восстановлении карточек из истории.
 * Обязательность превратила бы старую переписку в ошибку разбора.
 */
export const ChatActionSampleTask = z
  .object({
    id: UuidString,
    title: z.string().max(500),
    /** Номер задачи внутри проекта — как её называют люди («#12»). */
    sequence_id: z.number().int().positive().optional(),
    /** Имя основного проекта задачи. Данные, а не машинная строка. */
    project_name: z.string().max(200).nullable().optional(),
    /**
     * Код основного проекта: задачу в системе зовут `<код>-<номер>` (`TaskCard.tsx`),
     * и карточка обязана звать её так же. Без кода остаётся `#<номер>` — тот же
     * фолбэк, что у карточек списка.
     */
    project_code: z.string().max(20).nullable().optional(),
    /**
     * Срок задачи — вход в решение, а не украшение: понизить приоритет у
     * просроченной задачи и у задачи со сроком через месяц — разные решения.
     */
    due_date: z.string().nullable().optional(),
    /** Сторона «сейчас» — только для операций, где меняется одно скалярное поле. */
    current: ChatActionSampleCurrent.nullable().optional(),
    /**
     * B30 [F13] — исполнители задачи на момент предложения, не больше трёх имён.
     *
     * Это ПДн в теле черновика, и это осознанно: строка состава без исполнителя
     * не даёт принять решение («перенести срок этим пятнадцати» — чьим?), а
     * читает черновик только его автор (`user_id` в условии всех выборок).
     * Модели исполнители не отдаются вовсе — срез для неё остаётся `{id,title}`.
     */
    assignees: z
      .array(z.object({ user_id: UuidString, display_name: z.string().max(200) }).strict())
      .max(3)
      .optional(),
    /** Сколько исполнителей всего, если имён больше трёх. */
    assignees_total: z.number().int().nonnegative().optional(),
    /**
     * B30 [F5, вторая половина] — применима ли операция к этой строке. `false`
     * означает «нет права записи»: строка показывается, но в счёт «применится N»
     * не входит. Снимок на момент предложения, как и имена.
     */
    applicable: z.boolean().optional(),
  })
  .strict();
export type ChatActionSampleTask = z.infer<typeof ChatActionSampleTask>;

/**
 * W2 — черновик массовой операции (Tier 2). Карточка подтверждения (F3) читает
 * ровно это; исполнение — клик «Применить» → существующая ручка под JWT
 * пользователя с `draft_id` (I1/I2/I7). `task_ids` — материализованный список на
 * момент превью (§7 staleness: лейбл «зафиксировано на момент превью» + `created_at`).
 */
export const ConfirmTaskBulkAction = z
  .object({
    type: z.literal('confirm_task_bulk'),
    draft_id: UuidString,
    kind: ChatActionDraftKind,
    /** kind=task_bulk — операция; kind=task_bulk_comment — текст. */
    op: TaskBulkOp.optional(),
    comment_text: z.string().max(10_000).optional(),
    /** Список, который исполнится по клику (≤ 500 — В-2). */
    task_ids: z.array(UuidString).max(500),
    /** Сколько задач попало под выборку до усечения потолком. */
    matched_total: z.number().int().nonnegative(),
    /** Видимы И доступны на запись — столько применится. */
    will_apply: z.number().int().nonnegative(),
    /** Видимы, но без права записи — столько будет отказано (`denied_ids`). */
    will_deny: z.number().int().nonnegative(),
    sample: z.array(ChatActionSampleTask).max(5),
    warnings: z.array(ChatActionWarningCode),
    /** Фильтр, из которого собрана выборка (если не список id) — для «Показать все N». */
    filter_used: TaskFilterShared.optional(),
    /**
     * CIA-B7 — режим выборки, а не только правило отбора.
     *
     * `TaskFilterShared` признака «с закрытыми» не содержит: он живёт во входной
     * схеме инструмента (`SoftFilter.include_closed`) и до черновика не доезжал.
     * Предложение, собранное агентом с `include_closed: true`, при «Пересобрать»
     * считалось с дефолтным правилом закрытых задач — состав мог оказаться уже
     * исходного, и человек этого не видел: карточка выглядела так же. Поле
     * необязательное: у черновиков, созданных до него, режим по умолчанию —
     * закрытые скрыты, как и было.
     */
    filter_include_closed: z.boolean().optional(),
    created_at: IsoDateTime,
    expires_at: IsoDateTime,
  })
  .strict()
  .refine((a) => (a.kind === 'task_bulk' ? a.op !== undefined : a.comment_text !== undefined), {
    message: 'task_bulk requires op; task_bulk_comment requires comment_text',
    path: ['kind'],
  });
export type ConfirmTaskBulkAction = z.infer<typeof ConfirmTaskBulkAction>;

/** Навигация строго по whitelist-маршруту (SPA-переход, не full reload [П4]). */
export const NavigateAction = z
  .object({
    type: z.literal('navigate'),
    to: ChatNavigateRoute,
    project_id: UuidString.optional(),
  })
  .strict();
export type NavigateAction = z.infer<typeof NavigateAction>;

/**
 * Discriminated union без `z.discriminatedUnion`: у двух вариантов есть `.refine`,
 * а ZodEffects в discriminatedUnion не принимается. Обычный union с литералом
 * `type` даёт те же гарантии парсинга; фронт ветвится по `action.type`.
 */
export const UiAction = z.union([ApplyTaskFilterAction, ConfirmTaskBulkAction, NavigateAction]);
export type UiAction = z.infer<typeof UiAction>;

/** Имена типов действий — для сторожей и адаптера фронта. */
export const UiActionType = z.enum(['apply_task_filter', 'confirm_task_bulk', 'navigate']);
export type UiActionType = z.infer<typeof UiActionType>;

/**
 * Инструменты чата, чей результат несёт действие интерфейса (`UiAction`): stream-цикл бэкенда шлёт
 * по ним кадр `chat.ui_action`. Пак `llm-haiku-5-5`, Л-4 Р.2 (`LH-F15`) — перенесено из
 * `gateway/task-tools.ts` сюда: фронт по тому же списку решает, чем показать из истории ход без
 * текста (карточка черновика или шаг ленты), а не пустым ответом.
 */
export const UI_ACTION_TOOL_NAMES = ['apply_task_filter', 'propose_task_bulk'] as const;
export type UiActionToolName = (typeof UI_ACTION_TOOL_NAMES)[number];

/**
 * Действия, которые оставляют черновик (`chat_action_drafts`): из истории карточка восстанавливается
 * по черновику, и ход без текста с таким вызовом — ответ, а не «ответа нет».
 */
export const DRAFT_ACTION_TOOL_NAMES: readonly UiActionToolName[] = ['propose_task_bulk'];

// ─── Контекст поверхности задач в сообщении чата (§3.4, [Б14]) ─────

/**
 * `context.scope.tasks` — strict-подсхема: что видит пользователь на поверхности
 * задач в момент отправки. `selected_task_ids` — снимок выделения ТЕКУЩЕЙ
 * поверхности (≤ 100, остальное — `truncated` + честный `selected_count` [П9]).
 */
export const ChatTaskScope = z
  .object({
    surface: ChatTaskSurface,
    project_id: UuidString.optional(),
    selected_task_ids: z.array(UuidString).max(100),
    selected_count: z.number().int().nonnegative(),
    truncated: z.boolean(),
    active_filter: TaskFilterShared.nullable(),
    show_closed: z.boolean(),
  })
  .strict()
  .refine((s) => s.surface !== 'project' || s.project_id !== undefined, {
    message: 'project surface requires project_id',
    path: ['project_id'],
  })
  .refine((s) => s.selected_count >= s.selected_task_ids.length, {
    message: 'selected_count must cover selected_task_ids',
    path: ['selected_count'],
  })
  .refine((s) => s.truncated === s.selected_count > s.selected_task_ids.length, {
    message: 'truncated must reflect selected_count > selected_task_ids.length',
    path: ['truncated'],
  });
export type ChatTaskScope = z.infer<typeof ChatTaskScope>;

/**
 * Лейблы операций для заголовка карточки — ключи словаря §6.4 (`chat_action_op_*`).
 * Экспортируется, чтобы фронт и тест полноты словаря читали один перечень.
 */
export const CHAT_ACTION_OP_TYPES = TaskBulkOpType.options;

// ─── Черновик действия — строка таблицы `chat_action_drafts` (B5, §2.2) ────

/** Payload черновика — то же, что видит карточка, минус draft-метаданные. */
export const ChatActionDraftPayload = z
  .object({
    op: TaskBulkOp.optional(),
    comment_text: z.string().max(10_000).optional(),
    task_ids: z.array(UuidString).max(500),
    matched_total: z.number().int().nonnegative(),
    will_apply: z.number().int().nonnegative(),
    will_deny: z.number().int().nonnegative(),
    sample: z.array(ChatActionSampleTask).max(5),
    warnings: z.array(ChatActionWarningCode),
    filter_used: TaskFilterShared.optional(),
    /**
     * CIA-B7 — режим выборки, а не только правило отбора.
     *
     * `TaskFilterShared` признака «с закрытыми» не содержит: он живёт во входной
     * схеме инструмента (`SoftFilter.include_closed`) и до черновика не доезжал.
     * Предложение, собранное агентом с `include_closed: true`, при «Пересобрать»
     * считалось с дефолтным правилом закрытых задач — состав мог оказаться уже
     * исходного, и человек этого не видел: карточка выглядела так же. Поле
     * необязательное: у черновиков, созданных до него, режим по умолчанию —
     * закрытые скрыты, как и было.
     */
    filter_include_closed: z.boolean().optional(),
  })
  .strict();
export type ChatActionDraftPayload = z.infer<typeof ChatActionDraftPayload>;

/** Результат исполнения — зеркало ответа bulk-ручек (пишется на draft в B16). */
export const ChatActionDraftResult = z
  .object({
    affected: z.number().int().nonnegative(),
    denied_ids: z.array(UuidString),
    skipped_ids: z.array(z.object({ id: UuidString, reason: z.string() }).strict()),
    /**
     * B27 (заход 3) — строки состава, которые человек снял руками перед
     * применением («Применить к выделенным k из N», F19).
     *
     * Обязательно `.optional()`: схема строгая, а история переписки читает
     * черновики, записанные ДО появления поля. Обязательное поле сделало бы
     * старые карточки нечитаемыми. Полный состав (`k = N`) поле не пишет вовсе —
     * иначе отчёт сказал бы «исключено 0 вручную» там, где никто ничего не
     * исключал.
     */
    excluded_ids: z.array(UuidString).optional(),
  })
  .strict();
export type ChatActionDraftResult = z.infer<typeof ChatActionDraftResult>;

/**
 * Строка черновика в ответе history (B10) и для ActionCard (F8): замороженная
 * карточка рендерится по `status`/`result`, без активных кнопок (кроме живых
 * `proposed`). `entity_scope` наружу не отдаётся — служебный снимок.
 */
export const ChatActionDraft = z
  .object({
    id: UuidString,
    conversation_id: UuidString,
    kind: ChatActionDraftKind,
    payload: ChatActionDraftPayload,
    status: ChatActionDraftStatus,
    result: ChatActionDraftResult.nullable(),
    created_at: IsoDateTime,
    consumed_at: IsoDateTime.nullable(),
    expires_at: IsoDateTime,
  })
  .strict();
export type ChatActionDraft = z.infer<typeof ChatActionDraft>;

/**
 * chat-interactive-actions B18 [Л10.4] — срезанные аргументы tool-вызова в SSE.
 *
 * `propose_task_bulk` принимает до 500 id. Слать их во фрейм `chat.tool_call`
 * незачем ни одной стороне: фронт рисует по нему строку «готовлю предложение»,
 * а полный список приезжает отдельно — в `ui_action` и в самом черновике. Пятьсот
 * uuid в потоке — это ~19 КБ на кадр, который никто не читает.
 *
 * Форма честная: вместо массива идёт его длина и признак среза, а не молчаливо
 * укороченный список. Фронт по `truncated` понимает, что видит не всё.
 */
export const ChatToolArgsTrimmed = z
  .object({
    task_ids_count: z.number().int().nonnegative().optional(),
    truncated: z.literal(true),
  })
  .catchall(z.unknown());
export type ChatToolArgsTrimmed = z.infer<typeof ChatToolArgsTrimmed>;

/** TTL черновика — решение владельца В-5: 1 час. */
export const CHAT_ACTION_DRAFT_TTL_MS = 60 * 60 * 1000;
